import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  signal,
  input,
  DestroyRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConnectWiseService } from '../../../../../../core/services/connectwise.service';
import { cyflareTicketPriorities, cyflareTicketStatuses } from '../../../sample-data/data';
import { forkJoin, Observable, of } from 'rxjs';
import { tap, switchMap, map } from 'rxjs/operators';

// Kendo UI imports
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { IconsModule } from '@progress/kendo-angular-icons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { LoaderModule } from '@progress/kendo-angular-indicators';
import { NotificationService } from '@progress/kendo-angular-notification';
import { TabStripModule } from '@progress/kendo-angular-layout';
import { SwitchModule } from '@progress/kendo-angular-inputs';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { TenantMappingComponent } from '../../tenant-mapping/tenant-mapping.component';
import { IntegrationService } from '../../../../../../core/services/integration.service';
import { CyflareAutocompleteComponent } from '../../../../../../core/components/cyflare-autocomplete/cyflare-autocomplete.component';
import { ServiceTypeMappingComponent } from './components/service-type-mapping/service-type-mapping.component';
import {
  ServiceTypeMappingConfig,
  ServiceTypeMappingResponse,
  ServiceTypeMappingRequest,
  createDefaultMappingConfig,
  hasServiceTypeConfig,
} from './models/service-type-mapping.models';

// Define interfaces for mapping
interface PriorityMapping {
  id?: number;
  cyflareId: string;
  cyflareName: string;
  connectWiseId: string;
  connectWiseName: string;
}

interface StatusMapping {
  id?: number;
  cyflareId: string;
  cyflareName: string;
  connectWiseId: string;
  connectWiseName: string;
}

// Tab constants
const TABS = {
  CREDENTIALS: 'credentials',
  CONFIGURATION: 'configuration',
  TENANT_MAPPING: 'tenantMapping',
};

// Configuration status constants
const CONFIG_STATUS = {
  NOT_CONFIGURED: 'Not Configured',
  CONNECTION_CONFIGURED: 'Connection Configured',
  CONFIGURATION_CONFIGURED: 'Configuration Configured',
  CONFIGURED: 'Configured',
};

@Component({
  selector: 'app-connectwise-form',
  standalone: true,
  imports: [
    KENDO_DROPDOWNS,
    KENDO_LABEL,
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    IconsModule,
    InputsModule,
    LabelModule,
    DropDownsModule,
    LoaderModule,
    TabStripModule,
    SwitchModule,
    TenantMappingComponent,
    CyflareAutocompleteComponent,
    ServiceTypeMappingComponent,
  ],
  templateUrl: './connectwise-form.component.html',
  styleUrls: ['./connectwise-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [],
})
export class ConnectWiseFormComponent implements OnInit, OnChanges {
  // === Inputs / Outputs ===
  @Input() icons: any;
  @Input() loading = false;
  @Input() integrationStatus?: string | { id: number; name: string };
  @Input() integrationDetails: any = null;
  organizationId = input<number | null>(null);
  integrationId = input<number | null>(null);
  isCyflareUser = input<boolean>(false);
  @Output() cancel = new EventEmitter<void>();
  @Output() detailPkLoaded = new EventEmitter<number>();
  @Output() configurationSaved = new EventEmitter<void>();

  // === Internal state ===
  private integrationService = inject(IntegrationService);
  private _integrationDetails: any = null;

  savedBoardItem = signal<any>(null);
  savedCompanyItem = signal<any>(null);

  // Service type mapping configuration (static or dynamic mode)
  savedServiceTypeMappingConfig = signal<ServiceTypeMappingConfig>(createDefaultMappingConfig());

  // Integration details PK (for service type mapping API calls)
  integrationDetailsPk = signal<number | null>(null);

  // Track service type mapping backend IDs for save/delete logic
  private defaultServiceTypeMappingId: number | null = null;
  private originalSourceMappingIds: number[] = [];


  activeMainTab = TABS.CREDENTIALS;
  mappingActiveTab = 'priority';
  TABS = TABS;

  credentialsForm: FormGroup = this.fb.group({});
  boardSelectionForm: FormGroup = this.fb.group({ selectedBoard: [null, Validators.required] });
  companySelectionForm: FormGroup = this.fb.group({ selectedCompany: [null, Validators.required] });

  connectionTested = false;
  connectionSuccess = false;
  connectionMessage = '';

  connectWisePriorities: any[] = [];
  cyflareTicketPriorities = cyflareTicketPriorities;
  cyflarePriorities = cyflareTicketPriorities;
  connectWiseStatuses: any[] = [];
  cyflareTicketStatuses = cyflareTicketStatuses;
  cyflareStatuses = cyflareTicketStatuses;

  selectedCyflarePriority: any = null;
  selectedConnectWisePriority: any = null;
  selectedCyflareStatus: any = null;
  selectedConnectWiseStatus: any = null;

  priorityMappings: PriorityMapping[] = [];
  statusMappings: StatusMapping[] = [];
  originalPriorityMappings: PriorityMapping[] = [];
  originalStatusMappings: StatusMapping[] = [];

  mappedCompanies: any[] = [];
  savedConfiguration: any = null;
  configurationStatuses: any[] = [];
  statusMap: { [key: string]: number } = {};
  isEditMode = false;
  private statusesLoaded = false;

  private destroyRef = inject(DestroyRef);

  constructor(
    private fb: FormBuilder,
    private connectWiseService: ConnectWiseService,
    private cdr: ChangeDetectorRef,
    private notificationService: NotificationService
  ) {}

  // ============================================================
  // LIFECYCLE
  // ============================================================

  ngOnInit(): void {
    this.initCredentialsForm();
    this.setupFormChangeListener();
    this._integrationDetails = this.integrationDetails;
    this.loadStatusesThenInitialize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // React when integrationDetails arrives late (e.g., page refresh: parent API call completes after child init)
    if (changes['integrationDetails'] && !changes['integrationDetails'].firstChange && this.statusesLoaded) {
      this._integrationDetails = this.integrationDetails;
      this.onIntegrationDetailsReceived(this.integrationDetails);
    }
  }

  private initCredentialsForm(): void {
    this.credentialsForm = this.fb.group({
      companyId: ['', Validators.required],
      publicKey: ['', Validators.required],
      privateKey: ['', Validators.required],
      domain: ['', [Validators.required, Validators.pattern('^https?://.*')]],
      clientId: [''],
      supportsMultiTenant: [false],
    });
  }

  private setupFormChangeListener(): void {
    this.credentialsForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.isEditMode && this.connectionTested) {
        this.connectionTested = false;
        this.connectionSuccess = false;
        this.cdr.markForCheck();
      }
    });
  }

  private loadStatusesThenInitialize(): void {
    this.connectWiseService
      .getConfigurationStatuses()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (statuses) => {
          this.configurationStatuses = statuses;
          statuses.forEach((status) => {
            this.statusMap[status.name] = status.id;
          });
          this.statusesLoaded = true;
          this.onIntegrationDetailsReceived(this.integrationDetails);
        },
        error: () => {
          this.loading = false;
          this.showNotification('Failed to load configuration statuses. Please refresh the page.', 'error');
          this.cdr.markForCheck();
        },
      });
  }

  // ============================================================
  // EVENT 1: Integration details received from parent
  // ============================================================

  private onIntegrationDetailsReceived(details: any): void {
    this._integrationDetails = details;

    if (!details || details.integration === null) {
      this.onIntegrationNotConfigured();
      return;
    }
    this.onIntegrationExists(details);
  }

  // ============================================================
  // EVENT 2a: No configuration exists
  // ============================================================

  private onIntegrationNotConfigured(): void {
    this.loading = false;
    this.connectionTested = false;
    this.connectionSuccess = false;
    this.isEditMode = false;
    this.savedConfiguration = null;
    this.activeMainTab = TABS.CREDENTIALS;
    this.cdr.markForCheck();
  }

  // ============================================================
  // EVENT 2b: Configuration exists — route by status
  // ============================================================

  private onIntegrationExists(details: any): void {
    this.emitDetailPk(details);
    const statusName = this.resolveStatusName();

    switch (statusName) {
      case CONFIG_STATUS.CONFIGURED:
        this.onShowReadOnlyView(details);
        break;
      case CONFIG_STATUS.CONFIGURATION_CONFIGURED:
        this.onShowEditView(details, TABS.TENANT_MAPPING);
        break;
      case CONFIG_STATUS.CONNECTION_CONFIGURED:
        this.onShowEditView(details, TABS.CONFIGURATION);
        break;
      default:
        this.onIntegrationNotConfigured();
        break;
    }
  }

  // ============================================================
  // EVENT 3: Show read-only configured view
  // ============================================================

  private onShowReadOnlyView(details: any): void {
    this.populateBoardAndCompany(details);
    const boardData = this.loadBoardField(details);
    this.savedConfiguration = {
      username: details.ticket_integration_user || 'N/A',
      boardName: boardData ? `${boardData.id}) ${boardData.display_text || boardData.name}` : null,
      url: details.ticket_integration_url || 'N/A',
      supportsMultiTenant: details.is_multi_tenant || false,
    };
    this.loading = false;
    this.cdr.markForCheck();
  }

  // ============================================================
  // EVENT 4: Show edit view (credentials + config loaded)
  // ============================================================

  private onShowEditView(details: any, targetTab: string): void {
    this.isEditMode = true;
    this.loading = true;
    this.cdr.markForCheck();

    this.populateCredentialsForm(details);
    this.populateBoardAndCompany(details);
    this.connectionTested = true;
    this.connectionSuccess = true;

    if (targetTab === TABS.TENANT_MAPPING && details.is_multi_tenant) {
      this.activeMainTab = TABS.TENANT_MAPPING;
    } else {
      this.activeMainTab = targetTab;
    }

    const boardData = this.loadBoardField(details);
    this.loadConfigurationData(boardData);
  }

  // ============================================================
  // EVENT 5: Save connection (credentials tab)
  // ============================================================

  onSaveConnection(): void {
    if (!this.credentialsForm.valid) {
      this.credentialsForm.markAllAsTouched();
      this.showNotification('Please fill all required fields', 'error');
      return;
    }
    if (this.loading) return;

    this.loading = true;
    this.connectionSuccess = false;
    this.cdr.markForCheck();

    const credentials = this.credentialsForm.value;

    this.connectWiseService
      .testConnectionReal(this.organizationId()!, this.integrationId(), credentials)
      .pipe(
        switchMap(() => {
          this.connectionSuccess = true;
          return this.isEditMode
            ? this.updateExistingCredentials(credentials)
            : this.createNewCredentials(credentials);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => this.onConnectionSaveSuccess(response),
        error: () => this.onConnectionSaveError(),
      });
  }

  // ============================================================
  // EVENT 6: Connection save succeeded
  // ============================================================

  private onConnectionSaveSuccess(response: any): void {
    this.loading = false;
    this.connectionTested = true;
    const message = this.isEditMode ? 'Connection updated successfully' : 'Connection saved successfully';
    this.showNotification(message, 'success');

    if (this.isEditMode) {
      this._integrationDetails = null; // Invalidate cache for fresh reload
      this.loadSavedCredentialsAndConfig();
    } else {
      this.boardSelectionForm.reset();
      this.companySelectionForm.reset();
      this.loadPriorities();
    }

    this.activeMainTab = TABS.CONFIGURATION;
    this.cdr.markForCheck();
  }

  private onConnectionSaveError(): void {
    this.loading = false;
    this.connectionSuccess = false;
    // The interceptor will show the error notification
    this.cdr.markForCheck();
  }

  // ============================================================
  // EVENT 7: Save configuration (board/company/mappings tab)
  // ============================================================

  onSaveConfiguration(): void {
    if (!this.areMappingsComplete() || !this.boardSelectionForm.valid || !this.companySelectionForm.valid) {
      if (!this.boardSelectionForm.valid || !this.companySelectionForm.valid) {
        this.boardSelectionForm.markAllAsTouched();
        this.companySelectionForm.markAllAsTouched();
        this.showNotification('Please select both a board and a company', 'error');
      } else {
        this.showNotification('Please complete all field mappings before saving', 'error');
      }
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    this.getOrFetchIntegrationDetails()
      .pipe(
        switchMap((details) => this.updateIntegrationWithBoardAndCompany(details)),
        switchMap((integrationPk) =>
          this.replaceFieldMappings(integrationPk).pipe(map(() => integrationPk))
        ),
        switchMap((integrationPk) => this.saveServiceTypeMappings(integrationPk)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => this.onConfigurationSaveSuccess(),
        error: (err) => this.onConfigurationSaveError(err),
      });
  }

  // ============================================================
  // EVENT 8: Configuration save succeeded
  // ============================================================

  private onConfigurationSaveSuccess(): void {
    this.loading = false;
    this.showNotification('Configuration saved successfully', 'success');

    if (this.isMultiTenantEnabled()) {
      this.setMainTab(TABS.TENANT_MAPPING);
      this.cdr.markForCheck();
    } else {
      this.configurationSaved.emit();
    }
  }

  private onConfigurationSaveError(err: any): void {
    this.loading = false;
    console.error('Error saving configuration:', err);
    this.showNotification('Error saving configuration', 'error');
    this.cdr.markForCheck();
  }

  // ============================================================
  // EVENT 9: Edit configuration (from read-only to edit)
  // ============================================================

  onEditConfiguration(): void {
    this.integrationStatus = undefined;
    this.savedConfiguration = null;
    this._integrationDetails = null; // Force re-fetch
    this.loading = true;
    this.cdr.markForCheck();

    this.getOrFetchIntegrationDetails()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (details) => {
          if (!details || details.integration === null) {
            this.onIntegrationNotConfigured();
            return;
          }
          this.onShowEditView(details, TABS.CONFIGURATION);
        },
        error: () => {
          this.showNotification('Failed to load configuration', 'error');
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  // ============================================================
  // EVENT 10: Complete tenant mapping setup
  // ============================================================

  onCompleteTenantMappingSetup(): void {
    this.loading = true;
    this.cdr.markForCheck();

    const updateStatusRequest = {
      configuration_status: this.statusMap[CONFIG_STATUS.CONFIGURED],
    };

    this.connectWiseService
      .updateIntegrationStatus(this.organizationId()!, updateStatusRequest, this.integrationId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.loading = false;
          this.showNotification('Setup completed successfully', 'success');
          this.configurationSaved.emit();
        },
        error: () => {
          this.loading = false;
          this.showNotification('Failed to complete setup', 'error');
          this.cdr.markForCheck();
        },
      });
  }

  // ============================================================
  // SHARED HELPERS — Data extraction
  // ============================================================

  private resolveStatusName(): string {
    return typeof this.integrationStatus === 'object' ? this.integrationStatus.name : this.integrationStatus || '';
  }

  private emitDetailPk(details: any): void {
    if (details?.pk) {
      this.detailPkLoaded.emit(details.pk);
      this.integrationDetailsPk.set(details.pk);
    }
  }

  private populateCredentialsForm(details: any): void {
    const userParts = details.ticket_integration_user?.split('+') || [];
    this.credentialsForm.patchValue({
      companyId: userParts[0] || '',
      publicKey: userParts[1] || '',
      domain: details.ticket_integration_url,
      clientId: details.ticket_integration_auth_client_id || '',
      supportsMultiTenant: details.is_multi_tenant || false,
    });
  }

  private populateBoardAndCompany(details: any): void {
    const companyData = this.loadExtraField(details);
    if (companyData) {
      // Ensure display_text exists for CyflareAutocomplete (saved data may only have name)
      companyData.display_text = companyData.display_text || companyData.name;
      this.savedCompanyItem.set(companyData);
      this.companySelectionForm.patchValue({ selectedCompany: companyData.display_text });
    }
    const boardData = this.loadBoardField(details);
    if (boardData) {
      // Ensure display_text exists for CyflareAutocomplete (saved data may only have name)
      boardData.display_text = boardData.display_text || boardData.name;
      this.savedBoardItem.set(boardData);
      this.boardSelectionForm.patchValue({ selectedBoard: boardData.display_text });
    }
  }

  private loadConfigurationData(boardData: any): void {
    const priorities$ = this.connectWiseService.getPriorities(this.organizationId()!);
    const statuses$ = boardData
      ? this.connectWiseService.getStatuses(this.organizationId()!, boardData.id)
      : of([]);
    const detailsPk = this.integrationDetailsPk();
    // MF: Temporary gate — remove isCyflareUser check when GA release is ready for production
    const serviceTypeMappings$ = detailsPk && this.isCyflareUser()
      ? this.connectWiseService.getServiceTypeMappings(detailsPk)
      : of([] as ServiceTypeMappingResponse[]);

    forkJoin({ priorities: priorities$, statuses: statuses$, serviceTypeMappings: serviceTypeMappings$ })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ priorities, statuses, serviceTypeMappings }) => {
          this.connectWisePriorities = priorities;
          this.connectWiseStatuses = statuses;
          this.populateServiceTypeMappings(serviceTypeMappings);
          this.loadFieldMappings();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.showNotification('Failed to load configuration data', 'error');
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Populate service type mapping state from API responses.
   * Separates "Default" ticket_source from custom source mappings.
   */
  private populateServiceTypeMappings(apiMappings: ServiceTypeMappingResponse[]): void {
    this.defaultServiceTypeMappingId = null;
    this.originalSourceMappingIds = [];

    const defaultMapping = apiMappings.find((m) => m.ticket_source === 'Default');
    const customMappings = apiMappings.filter((m) => m.ticket_source !== 'Default');

    const config = createDefaultMappingConfig();

    if (defaultMapping) {
      this.defaultServiceTypeMappingId = defaultMapping.pk;
      config.defaultConfig = {
        type: defaultMapping.type,
        subtype: defaultMapping.subtype,
        item: defaultMapping.item,
      };
    }

    config.mappings = customMappings.map((m) => {
      this.originalSourceMappingIds.push(m.pk);
      return {
        id: m.pk,
        sourceValue: m.ticket_source,
        config: { type: m.type, subtype: m.subtype, item: m.item },
      };
    });

    this.savedServiceTypeMappingConfig.set(config);
  }

  private getOrFetchIntegrationDetails(): Observable<any> {
    if (this._integrationDetails && this._integrationDetails.integration !== null) {
      return of(this._integrationDetails);
    }
    return this.connectWiseService
      .getIntegrationDetails(this.organizationId()!, this.integrationId())
      .pipe(tap((details) => (this._integrationDetails = details)));
  }

  loadExtraField(details: any) {
    let companyData: any = null;
    if (details.field_map_extra_fields && details.field_map_extra_fields !== 'string') {
      try {
        companyData = JSON.parse(details.field_map_extra_fields);
      } catch (e) {
        console.error('Failed to parse company data:', e);
        if (
          typeof details.field_map_extra_fields === 'string' &&
          details.field_map_extra_fields.includes('"id"')
        ) {
          try {
            const jsonMatch = details.field_map_extra_fields.match(/\{.*\}/);
            if (jsonMatch) {
              companyData = JSON.parse(jsonMatch[0]);
            }
          } catch (parseError) {
            console.error('Failed to extract JSON from string:', parseError);
          }
        }
      }
    }
    return companyData;
  }

  loadBoardField(details: any) {
    let boardData: any = null;
    if (details.ticket_integration_board) {
      try {
        boardData = JSON.parse(details.ticket_integration_board);
      } catch (e) {
        console.error('Failed to parse board data:' + details.ticket_integration_board, e);
      }
    }
    return boardData;
  }

  // ============================================================
  // SHARED HELPERS — Save operations (flat Observable chains)
  // ============================================================

  private updateExistingCredentials(credentials: any): Observable<any> {
    return this.getOrFetchIntegrationDetails().pipe(
      tap((existingDetails) => this.emitDetailPk(existingDetails)),
      switchMap((existingDetails) => {
        const updatedDetails = this.buildCredentialsUpdatePayload(credentials, existingDetails);
        return this.connectWiseService.updateIntegrationDetails(existingDetails.pk, updatedDetails);
      })
    );
  }

  private createNewCredentials(credentials: any): Observable<any> {
    const saveRequest = {
      ...credentials,
      configuration_status: this.statusMap[CONFIG_STATUS.CONNECTION_CONFIGURED],
      is_multi_tenant: this.isMultiTenantEnabled(),
    };
    return this.connectWiseService.saveIntegrationDetails(this.organizationId()!, this.integrationId(), saveRequest);
  }

  private buildCredentialsUpdatePayload(credentials: any, existingDetails: any): any {
    return {
      organization_id: this.organizationId()!,
      integration: this.integrationId()!,
      ticket_integration_user: `${credentials.companyId}+${credentials.publicKey}`,
      ticket_integration_secret: credentials.privateKey,
      ticket_integration_url: credentials.domain,
      ticket_integration_auth_method: 'basic',
      ticket_integration_auth_url: credentials.domain,
      ticket_integration_auth_client_id: credentials.clientId || 'ff32be87-2ebb-4360-81d2-907a5b57f4bb',
      ticket_integration_auth_client_secret: '',
      field_map_subject: existingDetails.field_map_subject || 'summary',
      field_map_description: existingDetails.field_map_description || 'initialDescription',
      field_map_status_type: existingDetails.field_map_status_type || 'status',
      field_map_status_type_as_obj:
        existingDetails.field_map_status_type_as_obj !== undefined
          ? existingDetails.field_map_status_type_as_obj
          : true,
      field_map_priority: existingDetails.field_map_priority || 'priority',
      field_map_priority_as_obj:
        existingDetails.field_map_priority_as_obj !== undefined ? existingDetails.field_map_priority_as_obj : true,
      field_map_return_id: existingDetails.field_map_return_id || 'id',
      field_map_limit: existingDetails.field_map_limit || 'limit',
      field_map_ticket_id: existingDetails.field_map_ticket_id || 'id',
      field_map_extra_fields: existingDetails.field_map_extra_fields || '',
      field_map_query_fields: existingDetails.field_map_query_fields || null,
      ticket_integration_board: existingDetails.ticket_integration_board || null,
      configuration_status:
        typeof existingDetails.configuration_status === 'object'
          ? existingDetails.configuration_status.id
          : existingDetails.configuration_status || this.statusMap[CONFIG_STATUS.CONNECTION_CONFIGURED],
      is_multi_tenant: this.isMultiTenantEnabled(),
    };
  }

  private updateIntegrationWithBoardAndCompany(existingDetails: any): Observable<number> {
    const boardObj = this.savedBoardItem();
    const companyObj = this.savedCompanyItem();
    const updatedDetails = this.buildConfigurationUpdatePayload(existingDetails, boardObj, companyObj);

    return this.connectWiseService
      .updateIntegrationDetails(existingDetails.pk, updatedDetails)
      .pipe(map(() => existingDetails.pk));
  }

  private buildConfigurationUpdatePayload(existingDetails: any, boardObj: any, companyObj: any): any {
    const updatedDetails: any = {
      organization_id: this.organizationId()!,
      integration: this.integrationId()!,
      ticket_integration_board: boardObj != null ? JSON.stringify(boardObj) : '',
      field_map_extra_fields: companyObj != null ? JSON.stringify(companyObj) : '',
      configuration_status: this.isMultiTenantEnabled()
        ? this.statusMap[CONFIG_STATUS.CONFIGURATION_CONFIGURED]
        : this.statusMap[CONFIG_STATUS.CONFIGURED],
      is_multi_tenant: this.isMultiTenantEnabled(),
    };

    if (existingDetails.ticket_integration_user) {
      updatedDetails.ticket_integration_user = existingDetails.ticket_integration_user;
    }
    if (existingDetails.ticket_integration_url) {
      updatedDetails.ticket_integration_url = existingDetails.ticket_integration_url;
    }
    if (existingDetails.ticket_integration_auth_method) {
      updatedDetails.ticket_integration_auth_method = existingDetails.ticket_integration_auth_method;
    }
    if (existingDetails.ticket_integration_auth_url) {
      updatedDetails.ticket_integration_auth_url = existingDetails.ticket_integration_auth_url;
    }
    if (existingDetails.ticket_integration_auth_client_id) {
      updatedDetails.ticket_integration_auth_client_id = existingDetails.ticket_integration_auth_client_id;
    }

    updatedDetails.field_map_subject = existingDetails.field_map_subject || 'summary';
    updatedDetails.field_map_description = existingDetails.field_map_description || 'initialDescription';
    updatedDetails.field_map_status_type = existingDetails.field_map_status_type || 'status';
    updatedDetails.field_map_status_type_as_obj =
      existingDetails.field_map_status_type_as_obj !== undefined
        ? existingDetails.field_map_status_type_as_obj
        : true;
    updatedDetails.field_map_priority = existingDetails.field_map_priority || 'priority';
    updatedDetails.field_map_priority_as_obj =
      existingDetails.field_map_priority_as_obj !== undefined ? existingDetails.field_map_priority_as_obj : true;
    updatedDetails.field_map_return_id = existingDetails.field_map_return_id || 'id';
    updatedDetails.field_map_limit = existingDetails.field_map_limit || 'limit';
    updatedDetails.field_map_ticket_id = existingDetails.field_map_ticket_id || 'id';
    updatedDetails.field_map_query_fields = existingDetails.field_map_query_fields || '';

    return updatedDetails;
  }

  private replaceFieldMappings(integrationPk: number): Observable<any> {
    return this.connectWiseService.deleteAllIntegrationFieldValuesMap(this.organizationId()!).pipe(
      switchMap(() => {
        const creates = [
          ...this.priorityMappings.map((m) =>
            this.connectWiseService.createIntegrationFieldValuesMap(
              this.buildFieldValueData(m, 'Priority', integrationPk)
            )
          ),
          ...this.statusMappings.map((m) =>
            this.connectWiseService.createIntegrationFieldValuesMap(
              this.buildFieldValueData(m, 'Severity', integrationPk)
            )
          ),
        ];
        return creates.length > 0 ? forkJoin(creates) : of(null);
      })
    );
  }

  /**
   * Save service type mappings (default + custom source mappings).
   * - Default config: PUT if exists, POST if new, DELETE if cleared
   * - Custom mappings: delete all originals, then create all current
   */
  private saveServiceTypeMappings(integrationPk: number): Observable<any> {
    const config = this.savedServiceTypeMappingConfig();
    const ops: Observable<any>[] = [];

    const buildRequest = (ticketSource: string, cfg: typeof config.defaultConfig): ServiceTypeMappingRequest => ({
      integration_details: integrationPk,
      ticket_source: ticketSource,
      type: cfg.type,
      subtype: cfg.subtype,
      item: cfg.item,
    });

    // --- Default config ---
    const hasDefault = hasServiceTypeConfig(config.defaultConfig);
    if (hasDefault && this.defaultServiceTypeMappingId) {
      // Update existing default
      ops.push(
        this.connectWiseService.updateServiceTypeMapping(
          this.defaultServiceTypeMappingId,
          buildRequest('Default', config.defaultConfig)
        )
      );
    } else if (hasDefault && !this.defaultServiceTypeMappingId) {
      // Create new default
      ops.push(
        this.connectWiseService.createServiceTypeMapping(
          buildRequest('Default', config.defaultConfig)
        )
      );
    } else if (!hasDefault && this.defaultServiceTypeMappingId) {
      // Delete cleared default
      ops.push(this.connectWiseService.deleteServiceTypeMapping(this.defaultServiceTypeMappingId));
    }

    // --- Custom mappings: delete all originals, then create all current ---
    const deletes = this.originalSourceMappingIds.map((id) =>
      this.connectWiseService.deleteServiceTypeMapping(id)
    );

    const creates = config.mappings.map((mapping) =>
      this.connectWiseService.createServiceTypeMapping(
        buildRequest(mapping.sourceValue, mapping.config)
      )
    );

    // Execute: default op + deletes first, then creates
    const allDeleteOps: Observable<any>[] = [...ops, ...deletes];
    const deletePhase$: Observable<any> = allDeleteOps.length > 0 ? forkJoin(allDeleteOps) : of(null);

    return deletePhase$.pipe(
      switchMap(() => {
        return creates.length > 0 ? forkJoin(creates) : of(null);
      })
    );
  }

  private buildFieldValueData(mapping: any, type: string, integrationPk: number) {
    return {
      field_name: `${type}-${mapping.cyflareName}`,
      one_field_value: mapping.cyflareName,
      integration_field_value: mapping.connectWiseName,
      integration_field_type: 'str',
      use_as_default: true,
      integration: integrationPk,
    };
  }

  // Keep loadSavedCredentialsAndConfig as a convenience entry point for onConnectionSaveSuccess (edit mode)
  private loadSavedCredentialsAndConfig(): void {
    this.getOrFetchIntegrationDetails()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (details) => {
          if (!details || details.integration === null) {
            this.onIntegrationNotConfigured();
            return;
          }
          this.onShowEditView(details, TABS.CONFIGURATION);
        },
        error: () => {
          this.onIntegrationNotConfigured();
        },
      });
  }

  // ============================================================
  // DATA LOADING
  // ============================================================

  loadPriorities(): void {
    if (!this.organizationId()) {
      console.error('loadPriorities: No organization ID available');
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    this.connectWiseService
      .getPriorities(this.organizationId()!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (priorities) => {
          this.connectWisePriorities = priorities || [];
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Failed to load priorities:', error);
          this.loading = false;
          this.showNotification(
            `Failed to load priorities: ${error.message || 'Unknown error'}`,
            'error'
          );
          this.cdr.markForCheck();
        },
      });
  }

  loadStatuses(boardId: number): void {
    if (!this.organizationId() || !boardId) {
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    this.connectWiseService
      .getStatuses(this.organizationId()!, boardId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (statuses) => {
          this.connectWiseStatuses = statuses;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.showNotification('Failed to load statuses', 'error');
          this.cdr.markForCheck();
        },
      });
  }



  loadFieldMappings(): void {
    if (!this.organizationId()) return;

    this.connectWiseService
      .getIntegrationFieldMappings(this.organizationId()!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (mappings) => {
          this.priorityMappings = [];
          this.statusMappings = [];

          mappings.forEach((mapping) => {
            if (mapping.field_name.startsWith('Priority-')) {
              const cyflarePriorityName = mapping.one_field_value;
              const cyflarePriority = this.cyflarePriorities.find((p: any) => p.name === cyflarePriorityName);

              const connectWisePriorityName = mapping.integration_field_value;
              const connectWisePriority = this.connectWisePriorities.find(
                (p: any) => p.name === connectWisePriorityName
              );

              if (cyflarePriority && connectWisePriority) {
                this.priorityMappings.push({
                  id: mapping.pk,
                  cyflareId: cyflarePriority.id,
                  cyflareName: cyflarePriority.name,
                  connectWiseId: connectWisePriority.id,
                  connectWiseName: connectWisePriority.name,
                });
              }
            } else if (mapping.field_name.startsWith('Severity-')) {
              const cyflareStatusName = mapping.one_field_value;
              const cyflareStatus = this.cyflareStatuses.find((s: any) => s.name === cyflareStatusName);

              const connectWiseStatusName = mapping.integration_field_value;
              const connectWiseStatus = this.connectWiseStatuses.find(
                (s: any) => s.name === connectWiseStatusName
              );

              if (cyflareStatus && connectWiseStatus) {
                this.statusMappings.push({
                  id: mapping.pk,
                  cyflareId: cyflareStatus.id,
                  cyflareName: cyflareStatus.name,
                  connectWiseId: connectWiseStatus.id,
                  connectWiseName: connectWiseStatus.name,
                });
              }
            }
          });

          this.originalPriorityMappings = [...this.priorityMappings];
          this.originalStatusMappings = [...this.statusMappings];

          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Failed to load field mappings:', error);
        },
      });
  }

  // ============================================================
  // UI EVENT HANDLERS (board/company selection, tab switching, mappings)
  // ============================================================

  onBoardSelected(board: any): void {
    if (board && board.id) {
      // Normalize: API returns display_text, internal code uses name
      const normalized = { ...board, name: board.display_text || board.name, display_text: board.display_text || board.name };
      this.savedBoardItem.set(normalized);
      this.boardSelectionForm.patchValue({ selectedBoard: normalized.display_text });
      const previousMappings = [...this.statusMappings];

      this.loading = true;
      this.connectWiseService
        .getStatuses(this.organizationId()!, board.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (statuses) => {
            this.connectWiseStatuses = statuses;

            if (this.isEditMode && previousMappings.length > 0) {
              const newMappings: StatusMapping[] = [];

              previousMappings.forEach((mapping) => {
                const cyflareStillExists = this.cyflareStatuses.find((s: any) => s.id === mapping.cyflareId);
                const connectWiseStillExists = statuses.find((s: any) => s.id === mapping.connectWiseId);

                if (cyflareStillExists && connectWiseStillExists) {
                  newMappings.push(mapping);
                } else if (mapping.id) {
                  console.warn(
                    `Status mapping ${mapping.cyflareName} -> ${mapping.connectWiseName} is no longer valid for the new board`
                  );
                }
              });

              this.statusMappings = newMappings;
            } else {
              this.statusMappings = [];
            }

            this.loading = false;
            this.cdr.markForCheck();
          },
          error: () => {
            this.loading = false;
            this.showNotification('Failed to load statuses', 'error');
            this.statusMappings = previousMappings;
            this.cdr.markForCheck();
          },
        });
    }
  }

  onCompanySelected(company: any): void {
    if (company) {
      // Normalize: API returns display_text, internal code uses name
      const normalized = { ...company, name: company.display_text || company.name, display_text: company.display_text || company.name };
      this.savedCompanyItem.set(normalized);
      this.companySelectionForm.patchValue({ selectedCompany: normalized.display_text });
    }
  }

  /** Handle board cleared from CyflareAutocompleteComponent */
  onBoardCleared(): void {
    this.savedBoardItem.set(null);
    this.boardSelectionForm.patchValue({ selectedBoard: null });
    this.connectWiseStatuses = [];
    this.statusMappings = [];
    this.cdr.markForCheck();
  }

  /** Handle service type mapping config change from ServiceTypeMappingComponent */
  onServiceTypeMappingConfigChanged(config: ServiceTypeMappingConfig): void {
    this.savedServiceTypeMappingConfig.set(config);
    this.cdr.markForCheck();
  }

  // Tab switching
  setMainTab(tabId: string): void {
    this.activeMainTab = tabId;
    this.cdr.markForCheck();
  }

  isMainTabActive(tabId: string): boolean {
    return this.activeMainTab === tabId;
  }

  handleMainTabSelect(e: any): void {
    let selectedTab: string;

    switch (e.index) {
      case 0:
        selectedTab = TABS.CREDENTIALS;
        break;
      case 1:
        selectedTab = TABS.CONFIGURATION;
        break;
      case 2:
        selectedTab = TABS.TENANT_MAPPING;
        break;
      default:
        selectedTab = TABS.CREDENTIALS;
    }

    if (selectedTab === TABS.CONFIGURATION && !this.connectionSuccess) {
      e.preventDefault();
      return;
    }

    if (
      selectedTab === TABS.TENANT_MAPPING &&
      (!this.connectionSuccess || !this.isMultiTenantEnabled() || !this.integrationId())
    ) {
      e.preventDefault();
      return;
    }

    if (selectedTab === TABS.CONFIGURATION && this.connectionSuccess) {
      if (this.connectWisePriorities.length === 0) {
        this.loadPriorities();
      }
    }

    this.setMainTab(selectedTab);
  }

  setMappingTab(tabId: string): void {
    this.mappingActiveTab = tabId;
    this.cdr.markForCheck();
  }

  isMappingTabActive(tabId: string): boolean {
    return this.mappingActiveTab === tabId;
  }

  handleMappingTabSelect(e: any): void {
    let selectedTab: string;
    switch (e.index) {
      case 0:
        selectedTab = 'priority';
        break;
      case 1:
        selectedTab = 'status';
        break;
      case 2:
        selectedTab = 'serviceType';
        break;
      default:
        selectedTab = 'priority';
    }
    this.setMappingTab(selectedTab);
  }

  // Priority mapping methods
  selectCyflarePriority(priority: any): void {
    this.selectedCyflarePriority = priority;
    this.cdr.markForCheck();
  }

  selectConnectWisePriority(priority: any): void {
    this.selectedConnectWisePriority = priority;
    this.cdr.markForCheck();
  }

  addPriorityMapping(): void {
    if (this.selectedCyflarePriority && this.selectedConnectWisePriority) {
      const existingIndex = this.priorityMappings.findIndex((m) => m.cyflareId === this.selectedCyflarePriority.id);

      if (existingIndex >= 0) {
        this.priorityMappings[existingIndex] = {
          cyflareId: this.selectedCyflarePriority.id,
          cyflareName: this.selectedCyflarePriority.name,
          connectWiseId: this.selectedConnectWisePriority.id,
          connectWiseName: this.selectedConnectWisePriority.name,
        };
      } else {
        this.priorityMappings.push({
          cyflareId: this.selectedCyflarePriority.id,
          cyflareName: this.selectedCyflarePriority.name,
          connectWiseId: this.selectedConnectWisePriority.id,
          connectWiseName: this.selectedConnectWisePriority.name,
        });
      }

      this.selectedCyflarePriority = null;
      this.selectedConnectWisePriority = null;
      this.cdr.markForCheck();
    } else {
      this.showNotification('Please select both a Cyflare priority and a ConnectWise priority', 'error');
    }
  }

  removePriorityMapping(index: number): void {
    this.priorityMappings.splice(index, 1);
    this.cdr.markForCheck();
  }

  // Status mapping methods
  selectCyflareStatus(status: any): void {
    this.selectedCyflareStatus = status;
    this.cdr.markForCheck();
  }

  selectConnectWiseStatus(status: any): void {
    this.selectedConnectWiseStatus = status;
    this.cdr.markForCheck();
  }

  addStatusMapping(): void {
    if (this.selectedCyflareStatus && this.selectedConnectWiseStatus) {
      const existingIndex = this.statusMappings.findIndex((m) => m.cyflareId === this.selectedCyflareStatus.id);

      if (existingIndex >= 0) {
        this.statusMappings[existingIndex] = {
          cyflareId: this.selectedCyflareStatus.id,
          cyflareName: this.selectedCyflareStatus.name,
          connectWiseId: this.selectedConnectWiseStatus.id,
          connectWiseName: this.selectedConnectWiseStatus.name,
        };
      } else {
        this.statusMappings.push({
          cyflareId: this.selectedCyflareStatus.id,
          cyflareName: this.selectedCyflareStatus.name,
          connectWiseId: this.selectedConnectWiseStatus.id,
          connectWiseName: this.selectedConnectWiseStatus.name,
        });
      }

      this.selectedCyflareStatus = null;
      this.selectedConnectWiseStatus = null;
      this.cdr.markForCheck();
    } else {
      this.showNotification('Please select both a Cyflare status and a ConnectWise status', 'error');
    }
  }

  removeStatusMapping(index: number): void {
    this.statusMappings.splice(index, 1);
    this.cdr.markForCheck();
  }

  onTenantMappingSave(mappings: any[]): void {
    this.mappedCompanies = mappings;
    this.cdr.markForCheck();
  }

  // Mapping dropdown update methods
  getPriorityMappingFor(cyflarePriorityId: string): any {
    const mapping = this.priorityMappings.find((m) => m.cyflareId === cyflarePriorityId);
    if (mapping) {
      return this.connectWisePriorities.find((p) => p.id === mapping.connectWiseId);
    }
    return null;
  }

  updatePriorityMapping(cyflarePriority: any, connectWisePriority: any): void {
    if (!cyflarePriority || !connectWisePriority) return;

    const existingIndex = this.priorityMappings.findIndex((m) => m.cyflareId === cyflarePriority.id);

    if (existingIndex >= 0) {
      const existingId = this.priorityMappings[existingIndex].id;
      this.priorityMappings[existingIndex] = {
        id: existingId,
        cyflareId: cyflarePriority.id,
        cyflareName: cyflarePriority.name,
        connectWiseId: connectWisePriority.id,
        connectWiseName: connectWisePriority.name,
      };
    } else {
      this.priorityMappings.push({
        cyflareId: cyflarePriority.id,
        cyflareName: cyflarePriority.name,
        connectWiseId: connectWisePriority.id,
        connectWiseName: connectWisePriority.name,
      });
    }

    this.cdr.markForCheck();
  }

  getStatusMappingFor(cyflareStatusId: string): any {
    const mapping = this.statusMappings.find((m) => m.cyflareId === cyflareStatusId);
    if (mapping) {
      return this.connectWiseStatuses.find((s) => s.id === mapping.connectWiseId);
    }
    return null;
  }

  updateStatusMapping(cyflareStatus: any, connectWiseStatus: any): void {
    if (!cyflareStatus || !connectWiseStatus) return;

    const existingIndex = this.statusMappings.findIndex((m) => m.cyflareId === cyflareStatus.id);

    if (existingIndex >= 0) {
      const existingId = this.statusMappings[existingIndex].id;
      this.statusMappings[existingIndex] = {
        id: existingId,
        cyflareId: cyflareStatus.id,
        cyflareName: cyflareStatus.name,
        connectWiseId: connectWiseStatus.id,
        connectWiseName: connectWiseStatus.name,
      };
    } else {
      this.statusMappings.push({
        cyflareId: cyflareStatus.id,
        cyflareName: cyflareStatus.name,
        connectWiseId: connectWiseStatus.id,
        connectWiseName: connectWiseStatus.name,
      });
    }

    this.cdr.markForCheck();
  }

  // ============================================================
  // STATE CHECKERS
  // ============================================================

  areMappingsComplete(): boolean {
    const allPrioritiesMapped = this.cyflareTicketPriorities.every((priority) =>
      this.priorityMappings.some((mapping) => mapping.cyflareId === priority.id)
    );
    const allStatusesMapped = this.cyflareTicketStatuses.every((status) =>
      this.statusMappings.some((mapping) => mapping.cyflareId === status.id)
    );
    return allPrioritiesMapped && allStatusesMapped;
  }

  isMultiTenantEnabled(): boolean {
    return !!this.credentialsForm.get('supportsMultiTenant')?.value && this.integrationService.getCanBeMultitenant();
  }

  canBeMultitenant(): boolean {
    return this.integrationService.getCanBeMultitenant();
  }

  isConfigured(): boolean {
    const statusName = this.resolveStatusName();
    return statusName === CONFIG_STATUS.CONFIGURED;
  }

  hasFieldMappings(): boolean {
    return this.boardSelectionForm.valid && this.companySelectionForm.valid && this.areMappingsComplete();
  }

  hasTenantMappings(): boolean {
    return this.mappedCompanies && this.mappedCompanies.length > 0;
  }

  /** Check if a board has been selected */
  isBoardSelected(): boolean {
    return this.savedBoardItem() !== null;
  }

  cansavedata(): boolean {
    return (
      this.loading || !this.areMappingsComplete() || !this.boardSelectionForm.valid || !this.companySelectionForm.valid
    );
  }

  toggleMultiTenant(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (this.credentialsForm.get('supportsMultiTenant')) {
      this.credentialsForm.get('supportsMultiTenant')?.setValue(isChecked);
    } else {
      this.credentialsForm.addControl('supportsMultiTenant', this.fb.control(isChecked));
    }

    if (!isChecked && this.activeMainTab === TABS.TENANT_MAPPING) {
      this.setMainTab(TABS.CONFIGURATION);
    }

    this.cdr.markForCheck();
  }

  // ============================================================
  // NOTIFICATION
  // ============================================================

  private showNotification(message: string, type: 'success' | 'error' | 'warning' | 'info'): void {
    this.notificationService.show({
      content: message,
      cssClass: `notification-${type}`,
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: type, icon: true },
      closable: true,
      hideAfter: 3000,
    });
  }
}
